/**
 * Automatiska förslag på förberedelseplaner – EN delad beräkning för Viktigt, Idag och hanteringen, så
 * att siffrorna alltid stämmer överens. computeAutoDrafts (core) är ren och deterministisk per tillstånd
 * och kvart; här bestäms bara NÄR den körs:
 *  - nyckel = tillståndsobjektets identitet + kvarten av store.clock.now(); samma nyckel räknas aldrig om
 *  - beräkningen körs fördröjt (requestIdleCallback, annars setTimeout) och en generationsräknare kastar
 *    en körning som hunnit bli inaktuell
 *  - omräkning när tillståndet ändras (import, kalender och synk ger ett nytt objekt), när appen blir synlig
 *    igen (visibilitychange/pageshow) och när kvarten byts medan en vy visar förslagen – en enda timer för
 *    hela appen, ingen polling per post. Utkasten sparas aldrig, så en omräkning kan inte utlösa en ny.
 *
 * Granskningen av godkända planer (reviewPlan) delas på samma sätt via planReviews().
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { computeAutoDrafts, reviewPlan, Temporal, type AppState, type AutoDraft, type AutoDraftResult, type PlanReview } from '@autoday/core';
import { getStore, useStore, type AppStore } from '../store/appStore.ts';

export const QUARTER_MS = 15 * 60_000;
export const quarterOf = (ms: number): number => Math.floor(ms / QUARTER_MS);

/** Kör fn senare; returnerar en funktion som avbryter. */
export type Defer = (fn: () => void) => () => void;

export interface DraftsEngineDeps {
  getState: () => AppState;
  nowMs: () => number;
  compute: (state: AppState, nowMs: number) => AutoDraftResult;
  defer: Defer;
}

export interface DraftsEngine {
  /** Beställ en omräkning om nyckeln (tillstånd + kvart) har ändrats. Billig att anropa ofta. */
  refresh: () => void;
  readonly result: AutoDraftResult | null;
  subscribe: (l: () => void) => () => void;
}

/** Ren kärna (testas utan React och utan timers): cache, fördröjning och generationsräknare. */
export function createDraftsEngine(deps: DraftsEngineDeps): DraftsEngine {
  let cache: { state: AppState; quarter: number } | null = null;
  let pending: { state: AppState; quarter: number; cancel: () => void } | null = null;
  let result: AutoDraftResult | null = null;
  let generation = 0;
  const listeners = new Set<() => void>();
  return {
    refresh() {
      const state = deps.getState();
      const quarter = quarterOf(deps.nowMs());
      if (cache && cache.state === state && cache.quarter === quarter) return;
      if (pending && pending.state === state && pending.quarter === quarter) return;
      pending?.cancel();
      const gen = ++generation;
      pending = {
        state,
        quarter,
        cancel: deps.defer(() => {
          // En nyare beställning har gjorts sedan denna: kasta den.
          if (gen !== generation) return;
          pending = null;
          // Räkna på det aktuella underlaget och spara nyckeln för exakt det som räknades.
          const s = deps.getState();
          const nowMs = deps.nowMs();
          cache = { state: s, quarter: quarterOf(nowMs) };
          try {
            result = deps.compute(s, nowMs);
          } catch {
            // Trasigt underlag (t.ex. en ofullständig post från en äldre klient): behåll senaste resultatet.
            // Nyckeln är sparad, så samma underlag räknas inte om i en slinga.
            return;
          }
          for (const l of listeners) l();
        }),
      };
    },
    get result() {
      return result;
    },
    subscribe(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

const idle: Defer = (fn) => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    const h = window.requestIdleCallback(fn, { timeout: 400 });
    return () => window.cancelIdleCallback(h);
  }
  const h = setTimeout(fn, 16);
  return () => clearTimeout(h);
};

interface Watched {
  engine: DraftsEngine;
  users: number;
  stop: (() => void) | null;
}
const engines = new WeakMap<AppStore, Watched>();

function watchedFor(store: AppStore): Watched {
  let w = engines.get(store);
  if (!w) {
    const engine = createDraftsEngine({
      getState: () => store.state,
      nowMs: () => store.clock.now().epochMilliseconds,
      compute: (s, ms) => computeAutoDrafts(s, Temporal.Instant.fromEpochMilliseconds(ms)),
      defer: idle,
    });
    w = { engine, users: 0, stop: null };
    engines.set(store, w);
  }
  return w;
}

const pageVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** Bevakning medan minst en vy visar förslagen: tillstånd, synlighet och nästa kvart. */
function start(store: AppStore, engine: DraftsEngine): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!pageVisible()) return;
    const ms = store.clock.now().epochMilliseconds;
    timer = setTimeout(() => {
      engine.refresh();
      schedule();
    }, QUARTER_MS - (ms % QUARTER_MS) + 500);
  };
  const onVisible = () => {
    if (pageVisible()) engine.refresh();
    schedule();
  };
  const unsub = store.subscribe(() => engine.refresh());
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onVisible);
  engine.refresh();
  schedule();
  return () => {
    unsub();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onVisible);
    if (timer) clearTimeout(timer);
  };
}

/** Automatiska utkast enligt användarens regler. null tills första beräkningen är klar. */
export function usePrepDrafts(): { result: AutoDraftResult | null } {
  const store = getStore();
  const w = watchedFor(store);
  const result = useSyncExternalStore(w.engine.subscribe, () => w.engine.result, () => null);
  useEffect(() => {
    if (w.users++ === 0) w.stop = start(store, w.engine);
    return () => {
      if (--w.users === 0) {
        w.stop?.();
        w.stop = null;
      }
    };
  }, [store, w]);
  return { result };
}

/**
 * Aktuell kvart (store.clock) för en öppen förhandsvisning: ritar om när kvarten byts och när appen blir
 * synlig igen, så att ett förslag aldrig står kvar med tider som hunnit passera. En timer per öppen
 * förhandsvisning – ingen polling per post.
 */
export function useQuarter(): number {
  const store = getStore();
  const [q, setQ] = useState(() => quarterOf(store.clock.now().epochMilliseconds));
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      const ms = store.clock.now().epochMilliseconds;
      setQ(quarterOf(ms));
      if (pageVisible()) timer = setTimeout(tick, QUARTER_MS - (ms % QUARTER_MS) + 500);
    };
    tick();
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('pageshow', tick);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('pageshow', tick);
    };
  }, [store]);
  return q;
}

let reviewCache: { state: AppState; minute: number; map: ReadonlyMap<string, PlanReview> } | null = null;

/**
 * Granskning av alla aktiva planer, per källnyckel. Samma tillstånd och minut ger samma objekt, så Viktigt
 * och Idag visar samma siffror och vyerna räknar inte om vid varje omritning.
 */
export function planReviews(state: AppState, nowMs: number): ReadonlyMap<string, PlanReview> {
  const minute = Math.floor(nowMs / 60_000);
  if (reviewCache && reviewCache.state === state && reviewCache.minute === minute) return reviewCache.map;
  const now = Temporal.Instant.fromEpochMilliseconds(nowMs);
  const map = new Map<string, PlanReview>();
  for (const p of Object.values(state.prepPlans ?? {})) {
    if (p.status !== 'active') continue;
    const r = reviewPlan(state, p.id, now);
    if (r) map.set(p.sourceKey, r);
  }
  reviewCache = { state, minute, map };
  return map;
}

export function usePlanReviews(): ReadonlyMap<string, PlanReview> {
  const { state } = useStore();
  return planReviews(state, getStore().clock.now().epochMilliseconds);
}

/**
 * Syns källposten i Viktigt och i detaljvyn? Samma regel som kalenderns lista (core collectItems): posten
 * finns, är inte dold och dess kalender visas. Annat än så räknas inte – en siffra ska alltid leda någonstans.
 */
export function sourceVisible(state: AppState, sourceKey: string): boolean {
  const s = state.sourceItems[sourceKey];
  if (!s || s.archived) return false;
  const src = state.sources[s.sourceId];
  const cal = (src && state.calendars[src.calendarId]) || Object.values(state.calendars).find((c) => c.sourceId === s.sourceId);
  return !!cal && cal.visible;
}

export interface PrepAttention {
  /** Automatiska utkast att granska. */
  drafts: AutoDraft[];
  /** Godkända planer som behöver ses över. */
  reviews: PlanReview[];
}

/** Vad som väntar på användaren – samma underlag och samma siffror på Idag och i Viktigt. */
export function prepAttention(state: AppState, result: AutoDraftResult | null, reviews: ReadonlyMap<string, PlanReview>): PrepAttention {
  return {
    drafts: (result?.drafts ?? []).filter((d) => sourceVisible(state, d.sourceKey)),
    reviews: [...reviews.values()].filter((r) => r.needsReview && sourceVisible(state, r.sourceKey)),
  };
}

/** prepAttention för vyerna (Idag och Viktigt). */
export function usePrepAttention(): PrepAttention & { result: AutoDraftResult | null; reviewsByKey: ReadonlyMap<string, PlanReview> } {
  const { state } = useStore();
  const { result } = usePrepDrafts();
  const reviewsByKey = usePlanReviews();
  return { ...prepAttention(state, result, reviewsByKey), result, reviewsByKey };
}
